import React, { useState, useRef, useEffect } from 'react';
import { changePassword } from '../../../../services/auth';
import { toastActions } from '../../../../components/uielements/toast/toast';
import { IntlMessages } from '../../../../helper/langHelper';
import { LucideEye, LucideEyeOff, Check, X } from 'lucide-react';
import AdminModal from '../../../../components/uielements/modal/adminModal';

import {
  FormWrapper,
  FormSection,
  PasswordFieldGroup,
  Label,
  PasswordInputWrapper,
  StyledInputField,
  ToggleButton,
  PasswordMatchText,
  PasswordRequirementsSection,
  RequirementTitle,
  RequirementList,
  RequirementItem,
  ErrorText,
  ButtonContainer,
  PrimaryButton,
} from './ChangePasswordModal.styles';

const validatePasswordRequirements = (password) => {
  return {
    length: password.length >= 10,
    alphabetic: (password.match(/[a-zA-Z]/g) || []).length >= 6,
    numeric: (password.match(/[0-9]/g) || []).length >= 2,
    special: (password.match(/[^a-zA-Z0-9]/g) || []).length >= 2,
  };
};

const allRequirementsMet = (requirements) => {
  return (
    requirements.length && requirements.alphabetic && requirements.numeric && requirements.special
  );
};

const ChangePasswordModal = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    alphabetic: false,
    numeric: false,
    special: false,
  });

  const oldPasswordRef = useRef(null);

  // Focus old password field when modal opens
  useEffect(() => {
    if (isOpen && oldPasswordRef.current) {
      setTimeout(() => oldPasswordRef.current?.focus(), 150);
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Update password requirements only for new password field
    if (name === 'newPassword') {
      setPasswordRequirements(validatePasswordRequirements(value));
    }

    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  const validatePassword = () => {
    if (!formData.oldPassword || !formData.newPassword || !formData.confirmPassword) {
      setError('All password fields are required.');
      return false;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return false;
    }

    if (!allRequirementsMet(passwordRequirements)) {
      setError('Password does not meet security requirements.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validatePassword()) {
      return;
    }

    try {
      setLoading(true);

      await changePassword(formData.oldPassword, formData.newPassword);

      toastActions.success(<IntlMessages id="toast.password.change.success" />);

      setFormData({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
      });

      onClose();
    } catch (err) {
      toastActions.error(<IntlMessages id="toast.password.change.error" />);
      setError(err?.message || 'Password change failed. Please check your current password.');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setFormData({
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setError('');
    setPasswordRequirements({
      length: false,
      alphabetic: false,
      numeric: false,
      special: false,
    });
    onClose();
  };

  const passwordsMatch =
    formData.newPassword &&
    formData.confirmPassword &&
    formData.newPassword === formData.confirmPassword;
  const passwordsMismatch =
    formData.newPassword &&
    formData.confirmPassword &&
    formData.newPassword !== formData.confirmPassword;

  const footer = (
    <ButtonContainer>
      <PrimaryButton type="submit" disabled={loading || !allRequirementsMet(passwordRequirements)}>
        {loading ? 'Changing…' : 'Change Password'}
      </PrimaryButton>
    </ButtonContainer>
  );

  return (
    <AdminModal
      isOpen={isOpen}
      onClose={handleCloseModal}
      title="Change Password"
      footer={footer}
      width="520px"
    >
      <FormWrapper onSubmit={handleSubmit}>
        {error && <ErrorText>{error}</ErrorText>}

        <FormSection>
          {/* Current Password */}
          <PasswordFieldGroup>
            <Label htmlFor="oldPassword">Current Password</Label>
            <PasswordInputWrapper>
              <StyledInputField
                ref={oldPasswordRef}
                id="oldPassword"
                name="oldPassword"
                type={showOldPassword ? 'text' : 'password'}
                value={formData.oldPassword}
                onChange={handleInputChange}
                placeholder="Enter current password"
                disabled={loading}
                autoComplete="current-password"
              />
              <ToggleButton
                type="button"
                onClick={() => setShowOldPassword((v) => !v)}
                aria-label="Toggle current password visibility"
                disabled={loading}
                tabIndex={-1}
              >
                {showOldPassword ? <LucideEye /> : <LucideEyeOff />}
              </ToggleButton>
            </PasswordInputWrapper>
          </PasswordFieldGroup>

          {/* New Password */}
          <PasswordFieldGroup>
            <Label htmlFor="newPassword">New Password</Label>
            <PasswordInputWrapper>
              <StyledInputField
                id="newPassword"
                name="newPassword"
                type={showNewPassword ? 'text' : 'password'}
                value={formData.newPassword}
                onChange={handleInputChange}
                placeholder="Enter new password"
                disabled={loading}
                autoComplete="new-password"
              />
              <ToggleButton
                type="button"
                onClick={() => setShowNewPassword((v) => !v)}
                aria-label="Toggle new password visibility"
                disabled={loading}
                tabIndex={-1}
              >
                {showNewPassword ? <LucideEye /> : <LucideEyeOff />}
              </ToggleButton>
            </PasswordInputWrapper>
          </PasswordFieldGroup>

          {/* Confirm Password */}
          <PasswordFieldGroup>
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <PasswordInputWrapper>
              <StyledInputField
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Confirm new password"
                disabled={loading}
                autoComplete="new-password"
              />
              <ToggleButton
                type="button"
                onClick={() => setShowConfirmPassword((v) => !v)}
                aria-label="Toggle confirm password visibility"
                disabled={loading}
                tabIndex={-1}
              >
                {showConfirmPassword ? <LucideEye /> : <LucideEyeOff />}
              </ToggleButton>
            </PasswordInputWrapper>
            {passwordsMatch && (
              <PasswordMatchText match="success">✓ Passwords match</PasswordMatchText>
            )}
            {passwordsMismatch && (
              <PasswordMatchText match="error">✗ Passwords don&apos;t match</PasswordMatchText>
            )}
          </PasswordFieldGroup>
        </FormSection>

        {/* Password Requirements */}
        <PasswordRequirementsSection>
          <RequirementTitle>Requirements</RequirementTitle>
          <RequirementList>
            <RequirementItem met={passwordRequirements.length}>
              {passwordRequirements.length ? <Check strokeWidth={3} /> : <X strokeWidth={3} />}
              <span>10+ characters</span>
            </RequirementItem>
            <RequirementItem met={passwordRequirements.alphabetic}>
              {passwordRequirements.alphabetic ? <Check strokeWidth={3} /> : <X strokeWidth={3} />}
              <span>6+ letters</span>
            </RequirementItem>
            <RequirementItem met={passwordRequirements.numeric}>
              {passwordRequirements.numeric ? <Check strokeWidth={3} /> : <X strokeWidth={3} />}
              <span>2+ numbers</span>
            </RequirementItem>
            <RequirementItem met={passwordRequirements.special}>
              {passwordRequirements.special ? <Check strokeWidth={3} /> : <X strokeWidth={3} />}
              <span>2+ special chars</span>
            </RequirementItem>
          </RequirementList>
        </PasswordRequirementsSection>
      </FormWrapper>
    </AdminModal>
  );
};

export default ChangePasswordModal;
